/**
 * src/app/ui/CloneSourcePanel.ts
 *
 * Clone Source panel (Window > Clone Source) — Photoshop lets you park up
 * to 5 clone sources and, per source, adjust an Offset X/Y, a uniform Scale
 * percentage and a Rotation angle used when stamping, plus one global "Show
 * Overlay" toggle that previews the source under the brush cursor.
 *
 * This panel owns that state itself (5 slots + overlay flag) and is fully
 * interactive — no more "Coming soon" stub. It optionally reads the live
 * source point from a CloneStampTool instance (via the tool's existing
 * public `getSourcePoint()`), so "Set Source Here" can snapshot the point
 * the user last Alt+clicked on the canvas into the active slot.
 *
 * NOT yet wired (out of scope for this change, needs CloneStampTool.ts):
 *  - CloneStampTool has no multi-source array and no way for this panel to
 *    *push* a slot's point/offset/scale/rotation back into it (no public
 *    setter beyond the existing single `sourcePoint`), so actually painting
 *    with a non-zero scale/rotation, or cycling between the 5 stored
 *    sources, requires new fields/logic on CloneStampTool itself.
 *  - menus.ts:1518 ("Clone Source" menu item) still calls comingSoon() —
 *    swapping that to open this panel is a separate change.
 */
import type { CloneStampTool } from "../../core/tools/CloneStampTool";

export interface CloneSource {
  /** 0-based slot index (0..4), shown to the user as #1..#5. */
  readonly index: number;
  /** Captured document-space source position, or null if never set. */
  x: number | null;
  y: number | null;
  offsetX: number;
  offsetY: number;
  /** Uniform scale percentage (100 = original size). */
  scale: number;
  /** Rotation in degrees. */
  rotation: number;
}

const SOURCE_COUNT = 5;

function makeDefaultSource(index: number): CloneSource {
  return { index, x: null, y: null, offsetX: 0, offsetY: 0, scale: 100, rotation: 0 };
}

export class CloneSourcePanel {
  private container: HTMLElement;
  private tool: CloneStampTool | null;
  private sources: CloneSource[];
  private activeIndex = 0;
  private showOverlay = false;

  private sourcesEl: HTMLElement;
  private offsetXEl: HTMLInputElement;
  private offsetYEl: HTMLInputElement;
  private scaleEl: HTMLInputElement;
  private rotationEl: HTMLInputElement;
  private overlayEl: HTMLInputElement;

  constructor(tool?: CloneStampTool | null) {
    this.tool = tool ?? null;
    this.sources = Array.from({ length: SOURCE_COUNT }, (_, i) => makeDefaultSource(i));

    this.container = document.createElement("div");
    this.container.className = "clone-source-panel";
    this.container.innerHTML = `
      <div class="panel-title">Clone Source</div>
      <div class="panel-body clone-source-panel__body">
        <div class="clone-source-panel__sources" role="listbox" aria-label="Clone sources"></div>
        <div class="clone-source-panel__fields">
          <label class="opt">Offset X:
            <input type="number" class="cs-offset-x" step="1">
          </label>
          <label class="opt">Offset Y:
            <input type="number" class="cs-offset-y" step="1">
          </label>
          <label class="opt">Scale %:
            <input type="number" class="cs-scale" step="1" min="1">
          </label>
          <label class="opt">Rotation °:
            <input type="number" class="cs-rotation" step="1">
          </label>
          <div class="clone-source-panel__buttons">
            <button type="button" class="cs-capture">Set Source Here</button>
            <button type="button" class="cs-reset-transform" title="Reset scale, rotation and offset">Reset Transform</button>
          </div>
          <label class="opt">
            <input type="checkbox" class="cs-show-overlay"> Show Overlay
          </label>
        </div>
      </div>
    `;

    this.sourcesEl = this.container.querySelector(".clone-source-panel__sources") as HTMLElement;
    this.offsetXEl = this.container.querySelector(".cs-offset-x") as HTMLInputElement;
    this.offsetYEl = this.container.querySelector(".cs-offset-y") as HTMLInputElement;
    this.scaleEl = this.container.querySelector(".cs-scale") as HTMLInputElement;
    this.rotationEl = this.container.querySelector(".cs-rotation") as HTMLInputElement;
    this.overlayEl = this.container.querySelector(".cs-show-overlay") as HTMLInputElement;
    const captureBtn = this.container.querySelector(".cs-capture") as HTMLButtonElement;
    const resetBtn = this.container.querySelector(".cs-reset-transform") as HTMLButtonElement;

    this.renderSourceButtons();
    this.renderFields();

    this.offsetXEl.addEventListener("input", () => {
      this.activeSource.offsetX = Number(this.offsetXEl.value) || 0;
      this.emitChange();
    });
    this.offsetYEl.addEventListener("input", () => {
      this.activeSource.offsetY = Number(this.offsetYEl.value) || 0;
      this.emitChange();
    });
    this.scaleEl.addEventListener("input", () => {
      const v = Number(this.scaleEl.value);
      this.activeSource.scale = Number.isFinite(v) && v > 0 ? v : 100;
      this.emitChange();
    });
    this.rotationEl.addEventListener("input", () => {
      this.activeSource.rotation = Number(this.rotationEl.value) || 0;
      this.emitChange();
    });
    this.overlayEl.addEventListener("change", () => {
      this.showOverlay = this.overlayEl.checked;
      this.emitChange();
    });
    captureBtn.addEventListener("click", () => this.captureSourceFromTool());
    resetBtn.addEventListener("click", () => this.resetActiveTransform());
  }

  private get activeSource(): CloneSource {
    return this.sources[this.activeIndex]!;
  }

  private renderSourceButtons(): void {
    this.sourcesEl.innerHTML = this.sources.map((s) => {
      const active = s.index === this.activeIndex;
      const set = s.x !== null && s.y !== null;
      return `
        <button
          type="button"
          class="clone-source-panel__slot${active ? " is-active" : ""}${set ? " is-set" : ""}"
          data-index="${s.index}"
          role="option"
          aria-selected="${active}"
          title="Source ${s.index + 1}${set ? ` (${Math.round(s.x!)}, ${Math.round(s.y!)})` : " (not set)"}"
        >${s.index + 1}</button>
      `;
    }).join("");

    this.sourcesEl.querySelectorAll<HTMLButtonElement>(".clone-source-panel__slot").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.index);
        this.setActiveIndex(idx);
      });
    });
  }

  private renderFields(): void {
    const s = this.activeSource;
    this.offsetXEl.value = String(s.offsetX);
    this.offsetYEl.value = String(s.offsetY);
    this.scaleEl.value = String(s.scale);
    this.rotationEl.value = String(s.rotation);
    this.overlayEl.checked = this.showOverlay;
  }

  /** Select which of the 5 source slots subsequent field edits apply to. */
  setActiveIndex(index: number): void {
    if (index < 0 || index >= SOURCE_COUNT) return;
    this.activeIndex = index;
    this.renderSourceButtons();
    this.renderFields();
  }

  getActiveIndex(): number {
    return this.activeIndex;
  }

  /** Snapshot the CloneStampTool's current Alt+click source point into the active slot. */
  captureSourceFromTool(): void {
    const pt = this.tool?.getSourcePoint?.() ?? null;
    if (!pt) return;
    this.activeSource.x = pt.x;
    this.activeSource.y = pt.y;
    this.renderSourceButtons();
    this.emitChange();
  }

  /** Reset the active slot's offset/scale/rotation back to identity (position is kept). */
  resetActiveTransform(): void {
    const s = this.activeSource;
    s.offsetX = 0;
    s.offsetY = 0;
    s.scale = 100;
    s.rotation = 0;
    this.renderFields();
    this.emitChange();
  }

  /** Attach (or detach, with null/undefined) the live CloneStampTool this panel reads from. */
  setTool(tool: CloneStampTool | null | undefined): void {
    this.tool = tool ?? null;
  }

  /** Full read-only snapshot of all 5 source slots. */
  getSources(): ReadonlyArray<CloneSource> {
    return this.sources.map((s) => ({ ...s }));
  }

  getShowOverlay(): boolean {
    return this.showOverlay;
  }

  private emitChange(): void {
    // Let any host wiring (e.g. main.ts, once CloneStampTool grows the
    // matching multi-source support) react without this panel needing a
    // direct write-back reference beyond the optional `tool` field.
    this.container.dispatchEvent(
      new CustomEvent("pixelpa:clone-source-changed", {
        bubbles: true,
        detail: { index: this.activeIndex, source: { ...this.activeSource }, showOverlay: this.showOverlay },
      })
    );
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}
